import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { KeyboardContext } from "../../utilities/virtual-keyboard";
import { SET_VIRTUAL_KEYBOARD } from "../../store/actions/sessionActions";
import { TOGGLE_TIMEOUT_ACTIVE, TOGGLE_TIMEOUT_DISABLED } from "../../store/actions/timeoutActions";
import { getTouchScreen } from "../../store/sagas/selectors";
import { RecipientOption } from "./summaryExits";
import { filterRecipients, moveHighlight, selectHighlighted } from "./comboboxLogic";

// One integrated searchable picker shared by every recipient card (send-to-shop, send-to-salesperson), so
// the two cards behave identically. Typing filters the list in place — no separate search box. The
// behaviour that decides *who gets mailed* lives in the pure comboboxLogic.ts (unit-tested); this file
// is the DOM wiring, mirroring MenuSearch's click-outside + virtual-keyboard + idle-timeout pattern.

const Container = styled.div`
    position: relative;
    width: 100%;
`;

const Label = styled.label`
    font-family: ${props => props.theme.fontFamily};
    font-size: 12px;
    align-self: flex-start;
    color: ${props => props.theme.textColorSecondary};
    margin: 2px;
    margin-top: 8px;
    display: block;
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const Input = styled.input`
    ${({ theme }) => theme.bp.xs`
        font-size: 14px;
    `};
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}

    width: 100%;
    box-sizing: border-box;
    padding: 0px 10px;
    height: 40px;
    border-radius: 4px;
    border: 1px solid #d0d0d0;
    outline: none;
    transition: box-shadow 0.2s ease-in-out;
    @media (hover) {
        &:focus {
            box-shadow: 0px 0px 0px 1px #ccc inset;
        }
        &:hover {
            box-shadow: 0px 0px 0px 1px #ccc inset;
        }
    }
`;

// Wraps only the input so the absolutely-positioned list anchors to the input's bottom edge, not the
// Container top (which starts at the Label, ~20px higher — that offset was overlapping the list onto the input).
const InputWrapper = styled.div`
    position: relative;
    margin-bottom: 10px;
`;

const List = styled.ul`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 20;
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 180px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
`;

interface OptionProps {
    highlighted: boolean;
}

const Option = styled.li<OptionProps>`
    font-family: ${props => props.theme.fontFamily};
    font-size: 14px;
    padding: 8px 10px;
    cursor: pointer;
    text-align: left;
    color: ${props => props.theme.textColorSecondary};
    background: ${props => (props.highlighted ? "#eee" : "#fff")};
    &:hover {
        background: #eee;
    }
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const NoResults = styled.li`
    font-family: ${props => props.theme.fontFamily};
    font-size: 14px;
    padding: 8px 10px;
    color: rgba(0, 0, 0, 0.5);
    text-align: left;
`;

export const RecipientCombobox = ({
    label,
    searchPlaceholder,
    noResultsText,
    options,
    value,
    onChange,
}: {
    label: string;
    searchPlaceholder: string;
    noResultsText: string;
    options: RecipientOption[];
    value: RecipientOption | undefined;
    onChange: (recipient: RecipientOption) => void;
}) => {
    const dispatch = useDispatch();
    const VirtualKeyboard = useContext(KeyboardContext);
    const touchScreen = useSelector(getTouchScreen);
    const containerRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [highlight, setHighlight] = useState(-1);

    const domId = useMemo(() => `recipient-combobox-${Math.random().toString(36).slice(2)}`, []);
    const filtered = useMemo(() => filterRecipients(options, query), [options, query]);

    // When closed the box shows the committed recipient; while open it shows the type-ahead query. The sent
    // recipient is always `value` (resolved by effectiveRecipient upstream), never the query text.
    const displayValue = open ? query : value?.label ?? "";

    const toggleTouchKeyboard = (show: boolean) => {
        if (!touchScreen) {
            return;
        }
        if (show) {
            VirtualKeyboard.show((input: string) => onQueryChange(input), query);
            dispatch({ type: TOGGLE_TIMEOUT_DISABLED });
        } else {
            VirtualKeyboard.hide();
            dispatch({ type: TOGGLE_TIMEOUT_ACTIVE });
        }
        dispatch({ type: SET_VIRTUAL_KEYBOARD, showVirtualKeyboard: show });
    };

    const openList = () => {
        setOpen(true);
        setQuery("");
        setHighlight(-1);
    };

    // Close without selecting: the query is dropped and the input reverts to the committed recipient's
    // label, so what is shown always matches what would be sent.
    const closeList = () => {
        setOpen(false);
        setQuery("");
        setHighlight(-1);
        toggleTouchKeyboard(false);
    };

    const commit = (recipient: RecipientOption) => {
        onChange(recipient);
        setOpen(false);
        setQuery("");
        setHighlight(-1);
        toggleTouchKeyboard(false);
    };

    const onQueryChange = (next: string) => {
        setQuery(next);
        setOpen(true);
        // Highlight the first match so Enter picks it; an empty result set has no highlight, making Enter a
        // no-op rather than committing someone the query excluded.
        setHighlight(filterRecipients(options, next).length > 0 ? 0 : -1);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            setHighlight(current => moveHighlight(current, filtered.length, 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlight(current => moveHighlight(current, filtered.length, -1));
        } else if (event.key === "Enter") {
            event.preventDefault();
            const picked = selectHighlighted(filtered, highlight);
            if (picked) {
                commit(picked);
            }
        } else if (event.key === "Escape") {
            event.preventDefault();
            closeList();
        }
    };

    // Capture-phase document listener (same pattern as MenuSearch) so a click anywhere outside — including
    // the card's Send button — closes and reverts the box before that click's own handler runs.
    useEffect(() => {
        if (!open) {
            return;
        }
        const handleOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                closeList();
            }
        };
        document.addEventListener("click", handleOutside, true);
        return () => document.removeEventListener("click", handleOutside, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Container ref={containerRef}>
            <Label htmlFor={domId}>{label}</Label>
            <InputWrapper>
                <Input
                    id={domId}
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={`${domId}-list`}
                    aria-activedescendant={highlight >= 0 ? `${domId}-option-${highlight}` : undefined}
                    aria-autocomplete="list"
                    autoComplete="off"
                    placeholder={searchPlaceholder}
                    value={displayValue}
                    onFocus={() => {
                        openList();
                        toggleTouchKeyboard(true);
                    }}
                    onClick={() => setOpen(true)}
                    onChange={event => onQueryChange(event.target.value)}
                    onKeyDown={onKeyDown}
                />
                {open ? (
                    <List id={`${domId}-list`} role="listbox">
                        {filtered.length > 0 ? (
                            filtered.map((recipient, index) => (
                                <Option
                                    key={recipient.id}
                                    id={`${domId}-option-${index}`}
                                    role="option"
                                    aria-selected={index === highlight}
                                    highlighted={index === highlight}
                                    onMouseDown={event => event.preventDefault()}
                                    onClick={() => commit(recipient)}
                                >
                                    {recipient.label}
                                </Option>
                            ))
                        ) : (
                            <NoResults role="option" aria-disabled>
                                {noResultsText}
                            </NoResults>
                        )}
                    </List>
                ) : null}
            </InputWrapper>
        </Container>
    );
};
